import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { ALL_SECTIONS_SET } from 'config/sections';
import AuthCallback from './components/auth/AuthCallback';
import PasswordReset from './components/auth/PasswordReset';
import { CoolClimateNavigation } from './components/layout/CoolClimateNavigation';
import { Footer } from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { LocationShell } from './components/layout/LocationShell';
import { Navigation } from './components/layout/Navigation';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChartPngRegistryProvider } from './contexts/ChartPngRegistryContext';
import { LocationProvider, useLocation as useLocationHook } from './contexts/LocationContext';
import { PlatformProvider } from './contexts/PlatformContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { UserRoleProvider } from './contexts/UserRoleContext';
import { supabaseLocationService } from './services/supabaseLocationService';

const Overview = React.lazy(() => import('./components/pages/Overview').then(m => ({ default: m.Overview })));
const CoolClimateCalculator = React.lazy(() => import('./components/pages/CoolClimateCalculator').then(m => ({ default: m.CoolClimateCalculator })));
const SimpleDevelopersLogPage = React.lazy(() => import('./components/pages/SimpleDevelopersLogPage'));
const UserManagementPage = React.lazy(() => import('./components/pages/UserManagementPage'));
const ChangeLog = React.lazy(() => import('./components/pages/ChangeLog'));
const DeveloperPage = React.lazy(() => import('./components/pages/DeveloperPage'));
const LawImpactExplorerPage = React.lazy(() => import('./components/pages/LawImpactExplorerPage'));
const LocalAbatementScatterPage = React.lazy(() => import('./components/pages/LocalAbatementScatterPage'));
const PlatformPortalPage = React.lazy(() => import('./components/pages/PlatformPortalPage'));
const MembershipApplyPage = React.lazy(() => import('./components/pages/MembershipApplyPage'));
const MembershipRequestsAdminPage = React.lazy(() => import('./components/pages/MembershipRequestsAdminPage'));
const DemoRequestPage = React.lazy(() => import('./components/pages/DemoRequestPage'));
const DemoRequestsAdminPage = React.lazy(() => import('./components/pages/DemoRequestsAdminPage'));
const ProjectsAdminPage = React.lazy(() => import('./components/pages/ProjectsAdminPage'));
const OutreachAdminPage = React.lazy(() => import('./components/pages/OutreachAdminPage'));
const ProjectSubmitPage = React.lazy(() => import('./components/pages/ProjectSubmitPage'));
const ResearchDashboardPage = React.lazy(() => import('./components/pages/research/ResearchDashboardPage'));
const ResearchPapersPage = React.lazy(() => import('./components/pages/research/ResearchPapersPage'));
const ResearchPaperDetailPage = React.lazy(() => import('./components/pages/research/ResearchPaperDetailPage'));
const ResearchAssetsPage = React.lazy(() => import('./components/pages/research/ResearchAssetsPage'));
const ResearchDatasetsPage = React.lazy(() => import('./components/pages/research/ResearchDatasetsPage'));
const ResearchJournalsPage = React.lazy(() => import('./components/pages/research/ResearchJournalsPage'));
import { ResearchRoutes } from './components/pages/research-public/ResearchRoutes';

const LazyFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <div className="loading-spinner" />
  </div>
);


function LegacyRedirect({ section }: { section: string }) {
  const { selectedLocation } = useLocationHook();
  if (!selectedLocation) {
    return <Navigate to="/" replace />;
  }
  const slug = supabaseLocationService.getSlugForLocation(selectedLocation);
  if (!slug) {
    return <Navigate to="/" replace />;
  }
  const target = section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
  return <Navigate to={target} replace />;
}


function AppContent() {
  const { selectedLocation, selectLocation, locationData } = useLocationHook();
  const { loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isLandingPage = location.pathname === '/';
  const isMembershipApplyPage = location.pathname === '/membership/apply';
  const isMembershipAdminPage = location.pathname === '/admin/membership-requests';
  const isDemoPage = location.pathname === '/demo';
  const isDemoAdminPage = location.pathname === '/admin/demo-requests';
  const isProjectsSubmitPage = location.pathname === '/projects/submit';

  const isCoolClimatePage = location.pathname === '/coolclimate';
  const isPortalPage = location.pathname === '/portal';
  const isResearchRepoPage =
    location.pathname.startsWith('/research/municipal-transportation-emissions');

  const getSectionFromPath = (pathname: string) => {
    const parts = pathname.split('/').filter(Boolean);
    if (!parts.length) return 'overview';

    // Legacy path style: /ghg, /people, etc.
    if (ALL_SECTIONS_SET.has(parts[0])) {
      return parts[0];
    }

    // Slugged path style: /oakland/ghg, /oakland/people, etc.
    if (parts.length >= 2 && ALL_SECTIONS_SET.has(parts[1])) {
      return parts[1];
    }

    // Slug-only path defaults to overview.
    return 'overview';
  };

  const buildLocationPath = (slug: string | null, section: string) => {
    if (section === 'law-impact') return '/law-impact';
    if (section === 'projects') return '/projects';
    if (!slug) {
      return section === 'overview' ? '/overview' : `/${section}`;
    }
    return section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
  };

  const handleLocationSelect = (selectedLocationName: string) => {
    selectLocation(selectedLocationName);
    const slug = supabaseLocationService.getSlugForLocation(selectedLocationName);
    const section = getSectionFromPath(location.pathname);
    const nextPath = buildLocationPath(slug, section);
    const nextUrl = `${nextPath}${location.search || ''}${location.hash || ''}`;
    const currentUrl = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    if (currentUrl !== nextUrl) {
      navigate(nextUrl);
    }
  };

  const handleNavigateToOverview = () => {
    const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
    navigate(slug ? `/${slug}` : '/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionChange = (section: string) => {
    // Check if we're on Overview page and if it's fully loaded
    const isOnOverviewPage = location.pathname === '/' || location.pathname === '/overview';
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;
    
    // If on Overview page and not fully loaded, block navigation to pages other than Transportation
    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
    const nextPath = buildLocationPath(slug, section);
    const nextUrl = `${nextPath}${location.search || ''}${location.hash || ''}`;
    navigate(nextUrl);
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!locationData?.availableLocations?.length) return;
    const parts = location.pathname.split('/').filter(Boolean);
    if (!parts.length) return;
    const slug = parts[0];
    if (slug === 'coolclimate-calculator' || slug === 'research') return;
    const resolved = supabaseLocationService.peekLocationBySlug(slug);
    if (resolved && resolved.name !== selectedLocation) {
      selectLocation(resolved.name);
    }
  }, [location.pathname, locationData, selectedLocation, selectLocation]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const locationSlug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
  const showPrimaryNav =
    !isLandingPage && !isCoolClimatePage && !isPortalPage && !isMembershipApplyPage && !isMembershipAdminPage && !isDemoPage && !isDemoAdminPage && !isProjectsSubmitPage && !isResearchRepoPage;

  return (
    <div className={showPrimaryNav ? 'App App--primary-bottom-nav-mobile' : 'App'}>
      {/* Skip link for keyboard and screen reader users (ADA) */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Header 
        onNavigateToOverview={handleNavigateToOverview}
        onLocationSelect={handleLocationSelect}
        currentLocation={selectedLocation}
      />
      {showPrimaryNav && (
        <Navigation
          onSectionChange={handleSectionChange}
          locationSlug={locationSlug}
          currentPath={location.pathname}
        />
      )}
      {isCoolClimatePage && <CoolClimateNavigation />}
      <main id="main-content" key={theme} className="main-content" tabIndex={-1}>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route 
              path="/" 
              element={
                <Overview onLocationSelect={handleLocationSelect} isLandingPage={true} />
              } 
            />
            <Route
              path="/overview"
              element={<LegacyRedirect section="overview" />}
            />
            <Route path="/law-impact" element={<LawImpactExplorerPage />} />
            <Route path="/local-abatement-scatter" element={<LocalAbatementScatterPage />} />
            <Route path="/research/*" element={<ResearchRoutes />} />
            <Route
              path="/transportation-paper"
              element={<Navigate to="/research/municipal-transportation-emissions" replace />}
            />
            <Route
              path="/transportation-paper/"
              element={<Navigate to="/research/municipal-transportation-emissions" replace />}
            />
            <Route path="/portal" element={<PlatformPortalPage />} />
            <Route path="/membership/apply" element={<MembershipApplyPage />} />
            <Route path="/admin/membership-requests" element={<MembershipRequestsAdminPage />} />
            <Route path="/demo" element={<DemoRequestPage />} />
            <Route path="/admin/demo-requests" element={<DemoRequestsAdminPage />} />
            <Route path="/projects/submit" element={<ProjectSubmitPage />} />
            <Route path="/projects" element={<ProjectsAdminPage />} />
            <Route path="/admin/outreach" element={<OutreachAdminPage />} />
            <Route path="/admin/research" element={<ResearchDashboardPage />} />
            <Route path="/admin/research/papers" element={<ResearchPapersPage />} />
            <Route path="/admin/research/paper/:id" element={<ResearchPaperDetailPage />} />
            <Route path="/admin/research/assets" element={<ResearchAssetsPage />} />
            <Route path="/admin/research/datasets" element={<ResearchDatasetsPage />} />
            <Route path="/admin/research/journals" element={<ResearchJournalsPage />} />
            <Route
              path="/:locationSlug/*"
              element={<LocationShell onLocationSelect={handleLocationSelect} />}
            />
            <Route path="/people" element={<LegacyRedirect section="people" />} />
            <Route path="/ghg" element={<LegacyRedirect section="ghg" />} />
            <Route path="/buildings" element={<LegacyRedirect section="buildings" />} />
            <Route path="/transportation" element={<LegacyRedirect section="transportation" />} />
            <Route path="/policy-tool" element={<LegacyRedirect section="policy-tool" />} />
            <Route path="/community" element={<LegacyRedirect section="community" />} />
            <Route path="/changelog" element={<ChangeLog />} />
            <Route path="/other" element={<LegacyRedirect section="other" />} />
            <Route path="/admin-settings" element={<LegacyRedirect section="admin-settings" />} />
            <Route path="/coolclimate-calculator" element={<LegacyRedirect section="coolclimate-calculator" />} />
            <Route path="/analysis" element={<LegacyRedirect section="analysis" />} />
            <Route path="/coolclimate" element={<CoolClimateCalculator />} />
            <Route path="/developers-log" element={<SimpleDevelopersLogPage />} />
            <Route path="/user-management" element={<UserManagementPage />} />
            <Route path="/developer/:developerSlug" element={<DeveloperPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/reset-password" element={<PasswordReset />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      
      {/* Footer - Appears on all pages */}
      <Footer />
    </div>
  );
}

function AppWithUserRole() {
  const { user } = useAuth();
  
  // Always render AppContent, with or without user
  // UserRoleProvider will handle the case where userId is null/undefined
  return (
    <UserRoleProvider userId={user?.id} user={user ?? undefined}>
      <AppContent />
    </UserRoleProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <PlatformProvider>
        <LocationProvider>
          <ChartPngRegistryProvider>
            <Router>
              <ThemeProvider>
                <AppWithUserRole />
              </ThemeProvider>
            </Router>
          </ChartPngRegistryProvider>
        </LocationProvider>
      </PlatformProvider>
    </AuthProvider>
  );
}

export default App;